import React from "react";

import Button from "@mui/material/Button";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";

import { TrashIcon } from "@app/common/assets/icons";

import { ButtonProps } from "@rui/Button";
import { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

export const EditableContentWrapper = styled(Row)(({ theme }) => ({
  position: "relative",
  flex: 1,
  "&.focused": {
    "& .editable-content__actions": {
      display: "flex",
    },
    "& .editable-content__input-wrapper": {
      borderColor: theme.palette.shades.primary[500],
      backgroundColor: "#FFF",
    },
    zIndex: 4,
  },
  "&:not(.focused) .editable-content__input-wrapper:hover": {
    backgroundColor: theme.palette.transparent.black[4],
  },
}));

export const EditableSpanWrapper = styled(Row)(({ theme }) => ({
  padding: theme.spaces.small,
  ...theme.typography.bodyPrimaryReg,
  fontFamily: theme.typography.fontFamilySecondary,
  border: "1px solid transparent",
  borderRadius: theme.corners.small,
  width: "100%",
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
}));

export const EditableSpan = styled("span")(({ theme }) => ({
  ...theme.typography.bodyPrimaryReg,
  display: "inline-block",
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[700],
  overflowWrap: "anywhere",
  outline: "none",
  paddingRight: "0px",
  paddingLeft: "0px",
  position: "relative",
  width: "100%",
  "&:hover": {
    cursor: "text",
  },
  whiteSpace: "pre-wrap",
}));

export const EditActionsContainer = styled(Row)(({ theme }) => ({
  columnGap: theme.spaces.small,
  position: "absolute",
  bottom: -40,
  right: 0,
  zIndex: 10,
  width: "100%",
  "&.hidden": {
    zIndex: -1,
    display: "none",
  },
}));

export const EditActionButton = styled(Button)(({ theme }) => ({
  ...theme.typography.bodySecondaryMed,
  borderRadius: theme.corners.base,
  border: "1px solid",
  borderColor: theme.palette.transparent.black[8],
  backgroundColor: "#FFF",
  color: theme.palette.shades.neutral[500],
  height: "36px",
  padding: "8px 16px",

  "&.MuiButton-outlined:hover": {
    backgroundColor: "#fff",
  },

  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: theme.spaces.xsmall,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "12px",
    color: theme.palette.shades.neutral[300],
  },
}));

type EditActionIconButtonProps = IconButtonProps & { tooltip?: string };

const EditActionIconButton = styled<React.FC<EditActionIconButtonProps>>(
  ({ tooltip, ...props }) => (
    <TooltipWrapper title={tooltip} placement="bottom" enterDelay={1000}>
      <IconButton {...props} />
    </TooltipWrapper>
  )
)(({ theme }) => ({
  height: "36px",
  width: "36px",
  borderRadius: theme.corners.base,
  padding: theme.spaces.xsmall,
  border: "1px solid",
  borderColor: theme.palette.transparent.black[8],
  backgroundColor: "#FFF",
  "& .MuiSvgIcon-root": {
    fontSize: "14px",
    color: theme.palette.shades.neutral[500],
  },
  "&:hover": {
    backgroundColor: "#FFF",
  },
  "&.Mui-disabled": {
    backgroundColor: "#FFF",
    "& .MuiSvgIcon-root": {
      opacity: 0.5,
    },
  },
}));

export const SaveButton = styled<React.FC<ButtonProps>>((props) => (
  <EditActionButton variant="contained" color="primary" {...props}>
    Update
  </EditActionButton>
))(({ theme }) => ({
  color: "#fff",
  backgroundColor: theme.palette.shades.primary[500],
  borderColor: theme.palette.shades.primary[500],
}));

export const DiscardButton = styled<React.FC<ButtonProps>>((props) => (
  <EditActionButton variant="outlined" color="inherit" {...props}>
    Cancel
  </EditActionButton>
))(({ theme }) => ({
  "&:hover": {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    "& .MuiSvgIcon-root": {
      color: theme.palette.error.main,
    },
  },
}));

type DeleteButtonProps = Omit<IconButtonProps, "type"> & { type: string };

export const DeleteButton = styled<React.FC<DeleteButtonProps>>(
  ({ type, ...props }) => (
    <EditActionIconButton
      tooltip={props.disabled ? `${type} cannot be deleted` : `Delete ${type}`}
      {...props}
    >
      <TrashIcon />
    </EditActionIconButton>
  )
)(({ theme }) => ({
  "&:hover": {
    borderColor: theme.palette.error.main,
    "& .MuiSvgIcon-root": {
      color: theme.palette.error.main,
    },
  },
}));

export const Placeholder = styled("span")(({ theme }) => ({
  color: theme.palette.shades.neutral[300],
  whiteSpace: "nowrap",
  position: "absolute",
  "&.hidden": {
    zIndex: -1,
  },
}));
